// The peek's slide-in / slide-out choreography on the <aside>.
//
// Visibility is toggled with the `flex`/`hidden` utilities rather than an
// inline style. An inline `display` outranks any stylesheet rule, and Turbo
// caches this element's markup verbatim — a stale `display: none` would beat
// the docked rules and leave a 256px gutter with no menu in it. The aside
// carries `flex-col` but no `flex`, so `flex` is what gives it a column layout.
const DURATION_MS = 200

export function slideIn(menu: HTMLElement, animate: boolean) {
  menu.classList.remove('hidden')
  menu.classList.add('flex')
  menu.setAttribute('aria-hidden', 'false')

  if (!animate) {
    menu.classList.remove('-translate-x-full')
    menu.classList.add('translate-x-0')
    return
  }

  menu.offsetHeight // reflow, so the display change lands before the transition starts
  menu.classList.add('transition', 'ease-out', 'duration-200')
  requestAnimationFrame(() => {
    menu.classList.remove('-translate-x-full')
    menu.classList.add('translate-x-0')
  })
}

// `stillClosed` is consulted once the transition has run, so a menu reopened
// mid-slide is not hidden out from under itself.
export function slideOut(menu: HTMLElement, animate: boolean, stillClosed: () => boolean) {
  menu.setAttribute('aria-hidden', 'true')

  if (!animate) {
    menu.classList.remove('translate-x-0', 'flex')
    menu.classList.add('-translate-x-full', 'hidden')
    return
  }

  menu.classList.add('transition', 'ease-in', 'duration-200')
  menu.classList.remove('translate-x-0')
  menu.classList.add('-translate-x-full')
  setTimeout(() => {
    if (!stillClosed()) return
    menu.classList.remove('flex')
    menu.classList.add('hidden')
  }, DURATION_MS)
}

// The closed markup the layout ships, for Turbo to snapshot: a page restored
// from cache must not flash the menu open. The docked stylesheet re-shows a
// docked menu on its own.
export function resetToClosed(menu: HTMLElement, docked: boolean) {
  menu.classList.remove('flex', 'translate-x-0', 'transition', 'ease-in', 'ease-out', 'duration-200')
  menu.classList.add('hidden', '-translate-x-full')
  menu.setAttribute('aria-hidden', docked ? 'false' : 'true')
}
